import React, { useContext, useEffect, useRef, useCallback } from 'react';
import { BuildingContext } from '../../context/BuildingContext';
import useThreeScene from '../../hooks/useThreeScene';
import { setupLights, createGround } from '../../utils/rendering/lighting';
import { createWallsGroup } from '../../utils/geometry/facadeGeometry';
import { createLevelFloorsGroup } from '../../utils/geometry/buildingGeometry';
import { exportToVox } from '../../utils/export/voxExport';
import '../../styles/components/BuildGen.css';
import * as THREE from 'three';
import { getAvailableAssetTypes } from '../../utils/assetRegistry';

// Debounce function to limit how often a function can be called
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const BuildGen = ({ params = {} }) => {
  const { state } = useContext(BuildingContext);
  const { mountRef, scene } = useThreeScene();
  const rebuildingRef = useRef(false);
  const pendingUpdatesRef = useRef(false);
  
  const {
    levelHeight,
    groundFloorHeight,
    depth,
    showGround,
    levelNum,
    windowConfig,
    showLevelFloors,
    showLevelLines,
    facadeColor,
    windowTop,
    windowBottom,
    groundFloorColor,
    doorHeight,
    doorWidth,
    WindowColorA,
    WindowColorB,
    doorPosition,
    sideWallColor,
    showEdges,
    showBalconies, 
    balconyColor,
    balconyDepth,   // new parameter
    balconyHeight, 
    balconySideOffset,
    showAwnings,      // new
    awningColor,      // new
    awningDepth,      // new
    awningHeight,     // new
    awningSideOffset, // new
    showRoof,
    roofColor,
    roofAwning,     // Add this
    roofThickness,  // Add this
    showWindowFrames,
    windowFrameColor,  // Add this
    roofAccessColor,  // Add this
    showRoofAccess,   // Added for roof access toggle
    roofAccessWidth,
    roofAccessHeight,
    roofAccessDepth,
    roofAccessOffset,
    roofAccessDoorWidth,  // Add door width for roof access
    roofAccessDoorHeight, // Add door height for roof access
    showVitrine,     // new vitrine parameter
    vitrineColor,    // new vitrine parameter
    vitrineOffset,   // new vitrine offset parameter
    showVitrineFrames, // toggle for vitrine frames
    vitrineFrameDivision, // number of divisions for vitrine frames
    showVitrineAwning,  // new vitrine awning parameter
    vitrineAwningDepth, // new vitrine awning parameter
    vitrineAwningHeight, // new vitrine awning height
    vitrineAwningColor,  // new vitrine awning color
    vitrineFrameColor,
    windowInset,
    levelLinesColor,
    guardrailColor,
    showLeftSideWindows, // new side window parameter
    showRightSideWindows, // new side window parameter
    showBrickPattern, // new brick pattern toggle
    brickContrast, // new brick contrast slider value
    randomEraseBricks, // new random erase slider value
    randomEraseFine, // fine-grained random erasure value
    brickOnSideWalls, // toggle for brick pattern on side walls
    brickOnFacade, // toggle for brick pattern on facade
    brickOnGroundFloor, // toggle for brick pattern on ground floor
  } = state;

  // Ref to hold a reference to the entire scene for export
  const sceneRef = useRef(null);
  // Refs for individual geometry groups
  const geometryGroupRef = useRef(null);
  const floorsGroupRef = useRef(null);
  
  // Memoized function to build the 3D model
  const buildModel = useCallback(() => {
    if (rebuildingRef.current) {
      pendingUpdatesRef.current = true;
      return;
    }
    
    rebuildingRef.current = true;
    
    // Remove existing geometry groups but preserve ground
    scene.children = scene.children.filter(child => {
      // Always keep objects that are not our geometry groups
      if (child.name !== 'geometryGroup' && 
          child.name !== 'floorsGroup' && 
          child.name !== 'ground') {
        return true;
      }
      // Preserve ground, remove other groups
      return child.name === 'ground';
    });

    // Add ground if enabled (only if not already present)
    if (showGround && !scene.getObjectByName('ground')) {
      createGround(scene);
    } else if (!showGround && scene.getObjectByName('ground')) {
      // Remove ground if it should be hidden
      const ground = scene.getObjectByName('ground');
      if (ground) {
        scene.remove(ground);
      }
    }
    
    // Setup lights (only if not already present)
    setupLights(scene);

    // Add level floors if enabled
    if (showLevelFloors) {
      const floorsGroup = createLevelFloorsGroup({
        depth,
        levelNum,
        levelHeight,
        groundFloorHeight,
      });
      floorsGroup.name = 'floorsGroup';
      scene.add(floorsGroup);
      floorsGroupRef.current = floorsGroup;
    }

    // Create walls group with all parameters
    const wallsGroup = createWallsGroup({
      levelHeight,
      groundFloorHeight,
      depth,
      levelNum,
      windowConfig,
      showLevelLines,
      facadeColor,
      windowTop,
      windowBottom,
      groundFloorColor,
      doorHeight,
      doorWidth,
      WindowColorA,
      WindowColorB,
      doorPosition,
      sideWallColor,
      showEdges,
      showBalconies, 
      balconyColor,
      balconyDepth,
      balconyHeight,
      balconySideOffset,
      showAwnings,
      awningColor,
      awningDepth,
      awningHeight,
      awningSideOffset,
      showRoof,
      roofColor,
      roofAwning,
      roofThickness,
      showWindowFrames,
      windowFrameColor,
      roofAccessColor,
      showRoofAccess,
      roofAccessWidth,
      roofAccessHeight,
      roofAccessDepth,
      roofAccessOffset,
      roofAccessDoorWidth,  // Pass door width for roof access
      roofAccessDoorHeight, // Pass door height for roof access
      showVitrine,
      vitrineColor,
      vitrineOffset,
      showVitrineFrames,
      vitrineFrameDivision,
      vitrineFrameColor,
      showVitrineAwning,
      vitrineAwningDepth,
      vitrineAwningHeight,
      vitrineAwningColor,
      windowInset,
      levelLinesColor,
      guardrailColor,
      showLeftSideWindows,  // Pass left side windows toggle
      showRightSideWindows, // Pass right side windows toggle
      showBrickPattern,
      brickContrast,
      randomEraseBricks,
      randomEraseFine,
      brickOnSideWalls,
      brickOnFacade,
      brickOnGroundFloor,
    });
    wallsGroup.name = 'geometryGroup';
    scene.add(wallsGroup);
    
    // Store reference to the geometry group for export
    geometryGroupRef.current = wallsGroup;
    
    // Store reference to the entire scene for comprehensive export
    sceneRef.current = scene;
    
    // Reset flag and check for pending updates
    rebuildingRef.current = false;
    if (pendingUpdatesRef.current) {
      pendingUpdatesRef.current = false;
      requestAnimationFrame(() => buildModel());
    }
  }, [
    scene,
    levelHeight,
    groundFloorHeight,
    depth,
    levelNum,
    windowConfig,
    showLevelFloors,
    showLevelLines,
    facadeColor,
    windowTop,
    windowBottom,
    groundFloorColor,
    doorHeight,
    doorWidth,
    WindowColorA,
    WindowColorB,
    doorPosition,
    sideWallColor,
    showGround,
    showEdges,
    showBalconies,
    balconyColor,
    balconyDepth,
    balconyHeight,
    balconySideOffset,
    showAwnings,      // new
    awningColor,      // new
    awningDepth,      // new
    awningHeight,     // new
    awningSideOffset, // new
    showRoof,
    roofColor,
    roofAwning,     // Add this
    roofThickness,  // Add this
    showWindowFrames,
    windowFrameColor,  // Add this
    roofAccessColor,  // Add this
    showRoofAccess,   // Added for roof access toggle
    roofAccessWidth,
    roofAccessHeight,
    roofAccessDepth,
    roofAccessOffset,
    roofAccessDoorWidth,  // Pass door width for roof access
    roofAccessDoorHeight, // Pass door height for roof access
    showVitrine,     // new vitrine parameter
    vitrineColor,    // new vitrine parameter
    vitrineOffset,   // new vitrine offset parameter
    showVitrineFrames, // toggle for vitrine frames
    vitrineFrameDivision, // number of divisions for vitrine frames
    showVitrineAwning,  // new vitrine awning parameter
    vitrineAwningDepth, // new vitrine awning parameter
    vitrineAwningHeight, // new vitrine awning height
    vitrineAwningColor,  // new vitrine awning color
    vitrineFrameColor,
    windowInset,
    levelLinesColor,
    guardrailColor,
    showLeftSideWindows, // new side window parameter
    showRightSideWindows, // new side window parameter
    showBrickPattern,
    brickContrast,
    randomEraseBricks,
    randomEraseFine,
    brickOnSideWalls,
    brickOnFacade,
    brickOnGroundFloor,
  ]);

  // Debounced version to avoid too many rebuilds
  const debouncedBuildModel = useCallback(
    debounce(() => buildModel(), 150), // 150ms delay
    [buildModel]
  );

  // Build model on changes
  useEffect(() => {
    debouncedBuildModel();
  }, [
    debouncedBuildModel,
    levelHeight,
    groundFloorHeight,
    depth,
    levelNum,
    windowConfig,
    showLevelFloors,
    showLevelLines,
    facadeColor,
    windowTop,
    windowBottom,
    groundFloorColor,
    doorHeight,
    doorWidth,
    WindowColorA,
    WindowColorB,
    doorPosition,
    sideWallColor,
    showGround,
    showEdges,
    showBalconies,
    balconyColor,
    balconyDepth,
    balconyHeight,
    balconySideOffset,
    showAwnings,      // new
    awningColor,      // new
    awningDepth,      // new
    awningHeight,     // new
    awningSideOffset, // new
    showRoof,
    roofColor,
    roofAwning,     // Add this
    roofThickness,  // Add this
    showWindowFrames,
    windowFrameColor,  // Add this
    roofAccessColor,  // Add this
    showRoofAccess,   // Added for roof access toggle
    roofAccessWidth,
    roofAccessHeight,
    roofAccessDepth,
    roofAccessOffset,
    roofAccessDoorWidth,  // Add door width for roof access
    roofAccessDoorHeight, // Pass door height for roof access
    showVitrine,     // new vitrine parameter
    vitrineColor,    // new vitrine parameter
    vitrineOffset,   // new vitrine offset parameter
    showVitrineFrames, // toggle for vitrine frames
    vitrineFrameDivision, // number of divisions for vitrine frames
    showVitrineAwning,  // new vitrine awning parameter
    vitrineAwningDepth, // new vitrine awning parameter
    vitrineAwningHeight, // new vitrine awning height
    vitrineAwningColor,  // new vitrine awning color
    vitrineFrameColor,
    windowInset,
    levelLinesColor,
    guardrailColor,
    showLeftSideWindows, // new side window parameter
    showRightSideWindows, // new side window parameter
    showBrickPattern,
    brickContrast,
    randomEraseBricks,
    randomEraseFine,
    brickOnSideWalls,
    brickOnFacade,
    brickOnGroundFloor,
  ]);

  // Function to handle export to MagicaVoxel
  const handleExportToVox = () => {
    // Create a temporary group to hold all building elements for export
    const exportGroup = new THREE.Group();
    exportGroup.name = "buildingExport";
    
    // Define width - same as used in facadeGeometry.js (hardcoded to 42)
    const width = 42;
    
    // Add geometry elements first
    if (geometryGroupRef.current) {
      const clonedGeometry = geometryGroupRef.current.clone();
      
      // Track vitrine elements to ensure we're exporting them
      let vitrineFrameCount = 0;
      let vitrineFillCount = 0;
      
      // Get material color values for vitrine detection
      const getVitrineColorValues = () => {
        // Try to detect vitrine material colors from the state
        const vitrineColorValue = vitrineColor.replace('#', '').toLowerCase();
        const vitrineFrameColorValue = vitrineFrameColor.replace('#', '').toLowerCase();
        return { vitrineColorValue, vitrineFrameColorValue };
      };
      
      const { vitrineColorValue, vitrineFrameColorValue } = getVitrineColorValues();
      
      // Traverse and rename key elements to ensure they're properly identified
      clonedGeometry.traverse(object => {
        // Tag all window and door elements to ensure they get exported
        if (object.isMesh && object.material && object.material.color) {
          // Convert color to hex for comparison
          const colorHex = object.material.color.getHexString().toLowerCase();
          
          // Detect vitrine elements by their specific color
          if (colorHex === vitrineColorValue) {
            object.name = "vitrine_element";
            vitrineFillCount++;
            return;
          }
          
          // Detect vitrine frame elements by their specific color
          if (colorHex === vitrineFrameColorValue) {
            object.name = "vitrine_frame_element";
            vitrineFrameCount++;
            return;
          }
          
          // Check based on position and geometry
          if (object.position.z === 0 || object.position.z === (depth - 1)) {
            // Front or back facade
            if (object.position.y > groundFloorHeight) {
              // Window elements are typically above ground floor
              object.name = "window_element";
            } else if (object.position.y < doorHeight) {
              // Special treatment for ground floor elements
              if (object.position.z === (depth - 1)) {
                // Back facade - this could be vitrine or door
                if (object.position.x >= 0 && object.position.x < width) {
                  // Determine the door position
                  const doorX = doorPosition === 'left' 
                    ? 1 
                    : (doorPosition === 'right' 
                      ? width - doorWidth - 1 
                      : Math.floor((width - doorWidth) / 2));
                  
                  // Check if we're in the door area
                  const isInDoorArea = object.position.x >= doorX && 
                                      object.position.x < doorX + doorWidth;
                  
                  if (isInDoorArea) {
                    object.name = "door_element";
                  } else {
                    // This is a vitrine element - check if it's a frame or fill
                    // Check if this is a frame position
                    const x = Math.round(object.position.x);
                    const y = Math.round(object.position.y);
                    
                    const isLeftEdge = x === vitrineOffset;
                    const isRightEdge = x === width - vitrineOffset - 1;
                    const isTopEdge = y === doorHeight - 1;
                    const isBottomEdge = y === 0;
                    const isDoorAdjacentLeft = Math.abs(x - (doorX - vitrineOffset - 1)) < 0.5;
                    const isDoorAdjacentRight = Math.abs(x - (doorX + doorWidth + vitrineOffset)) < 0.5;
                    
                    // Check if this could be a divider in multi-division frames
                    let isDivider = false;
                    if (showVitrineFrames && vitrineFrameDivision > 1) {
                      // Define the left and right sections
                      let sectionWidth = 0;
                      let sectionStart = 0;
                      
                      if (x < doorX - vitrineOffset) {
                        // Left section
                        sectionWidth = doorX - vitrineOffset * 2;
                        sectionStart = vitrineOffset;
                      } else {
                        // Right section
                        sectionWidth = width - vitrineOffset - (doorX + doorWidth + vitrineOffset);
                        sectionStart = doorX + doorWidth + vitrineOffset;
                      }
                      
                      if (sectionWidth > 0) {
                        const spacing = Math.floor(sectionWidth / vitrineFrameDivision);
                        if (spacing > 0) {
                          for (let i = 1; i < vitrineFrameDivision; i++) {
                            if (Math.abs(x - (sectionStart + i * spacing)) < 0.5) {
                              isDivider = true;
                              break;
                            }
                          }
                        }
                      }
                    }
                    
                    const isFrame = showVitrineFrames && (
                      isLeftEdge || isRightEdge || isTopEdge || isBottomEdge || 
                      isDoorAdjacentLeft || isDoorAdjacentRight || isDivider
                    );
                    
                    if (isFrame) {
                      object.name = "vitrine_frame_element";
                      vitrineFrameCount++;
                    } else {
                      object.name = "vitrine_element";
                      vitrineFillCount++;
                    }
                  }
                }
              } else {
                // Front facade - this is likely a door
                object.name = "door_element";
              }
            }
          }
        }
      });
      
      console.log(`Tagged ${vitrineFrameCount} vitrine frames and ${vitrineFillCount} vitrine fills`);
      exportGroup.add(clonedGeometry);
    }
    
    // Add floor elements if they exist
    if (floorsGroupRef.current) {
      exportGroup.add(floorsGroupRef.current.clone());
    }
    
    // Export the combined group
    if (exportGroup.children.length > 0) {
      console.log("Exporting building with", exportGroup.children.length, "major components");
      exportToVox(exportGroup);
    } else {
      console.error('No building geometry to export');
    }
  };

  return (
    <div className="buildgen-container">
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
      <div className="title-overlay">
        <div className="title-dropdown">
          <div className="title-text">Classic Building</div>
          <div className="dropdown-content">
            {getAvailableAssetTypes().map(assetType => (
              <a 
                key={assetType.id}
                onClick={() => window.navigateTo(assetType.route)}
                className={assetType.comingSoon ? 'coming-soon' : ''}
              >
                {assetType.name}
                {assetType.comingSoon && ' (Coming Soon)'}
              </a>
            ))}
          </div> 
        </div>
      </div>
      <div className="export-overlay">
        <button 
          className="export-button"
          onClick={handleExportToVox}
          title="MagicaVoxel is a free voxel software that you can use to export optimized meshes like OBJ, STL and more"
        >
          Export .Vox File
        </button>
      </div>
    </div>
  );
};

export default React.memo(BuildGen);